import { useEffect, useState } from 'preact/hooks';
import {
  listSchedules,
  deleteSchedule,
  enableSchedule,
  disableSchedule,
  triggerSchedule,
} from '../lib/api';
import { activeToken } from '../store/global';
import type { Schedule } from '../types/agno-api';
import { PageHeader } from '../components/PageHeader';
import { DataTable } from '../components/DataTable';
import { ScheduleEditor } from '../components/ScheduleEditor';
import { formatRelative } from '../lib/format';

export function Schedules() {
  const [items, setItems] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Schedule | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);

  const refresh = async () => {
    if (!activeToken.value) return;
    setLoading(true);
    setError(null);
    try {
      const res = await listSchedules();
      const data = Array.isArray(res) ? res : (res as { data: Schedule[] }).data || [];
      setItems(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [activeToken.value]);

  const remove = async (id: string) => {
    if (!confirm('Excluir este agendamento?')) return;
    try {
      await deleteSchedule(id);
      refresh();
    } catch (e) {
      alert(`Não foi possível excluir: ${(e as Error).message}`);
    }
  };

  const toggleEnabled = async (s: Schedule) => {
    try {
      if (s.enabled) await disableSchedule(s.id);
      else await enableSchedule(s.id);
      refresh();
    } catch (e) {
      alert(`Não foi possível alternar o estado: ${(e as Error).message}`);
    }
  };

  const trigger = async (id: string) => {
    if (!confirm('Disparar agora? A execução roda fora do cron.')) return;
    try {
      const r = await triggerSchedule(id);
      alert(`Disparado.${r.run_id ? `\nrun_id: ${r.run_id}` : ''}`);
    } catch (e) {
      alert(`Não foi possível disparar: ${(e as Error).message}`);
    }
  };

  const openNew = () => {
    setEditing(null);
    setEditorOpen(true);
  };

  const openEdit = (s: Schedule) => {
    setEditing(s);
    setEditorOpen(true);
  };

  return (
    <div class="mx-auto max-w-7xl px-6 py-6">
      <PageHeader
        title="Agendamentos"
        description="Tarefas recorrentes executadas pelo agente. Cron, retries e payload por entrada."
        icon={<i class="ph ph-clock text-xl" />}
        actions={
          <div class="flex items-center gap-2">
            <button
              class="inline-flex items-center justify-center gap-2 h-8 px-4 rounded-md text-sm font-medium bg-background text-foreground border border-input shadow-xs hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50 disabled:pointer-events-none"
              onClick={refresh}
              disabled={loading}
            >
              {loading ? 'Atualizando…' : 'Atualizar'}
            </button>
            <button
              class="inline-flex items-center justify-center gap-2 h-8 px-4 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50 disabled:pointer-events-none"
              onClick={openNew}
              disabled={!activeToken.value}
            >
              <i class="ph ph-plus text-sm" /> Novo
            </button>
          </div>
        }
      />

      {error && (
        <div class="mb-3 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 text-foreground px-4 py-3 text-sm">
          <i class="ph ph-warning-circle text-base text-destructive shrink-0 mt-px" />
          <span class="font-mono break-words">{error}</span>
        </div>
      )}

      <DataTable
        rows={items as unknown as Array<Record<string, unknown>>}
        rowKey={(r) => (r as unknown as Schedule).id}
        columns={[
          {
            key: 'name',
            label: 'Nome',
            render: (r) => {
              const s = r as unknown as Schedule;
              return (
                <div>
                  <div class="font-medium">{s.name || s.id}</div>
                  {s.description && (
                    <div class="text-xs text-muted-foreground">{s.description}</div>
                  )}
                  {s.endpoint && (
                    <div class="text-xs text-muted-foreground font-mono mt-0.5">
                      {s.method || 'POST'} {s.endpoint}
                    </div>
                  )}
                </div>
              );
            },
          },
          {
            key: 'cron',
            label: 'Cron',
            render: (r) => {
              const s = r as unknown as Schedule;
              return (
                <div>
                  <code class="font-mono text-xs tabular-nums">{s.cron_expr || s.cron || '—'}</code>
                  {s.timezone && (
                    <div class="text-xs text-muted-foreground">{s.timezone}</div>
                  )}
                </div>
              );
            },
            width: '20ch',
          },
          {
            key: 'next',
            label: 'Próxima',
            render: (r) => (
              <span class="text-xs text-muted-foreground tabular-nums">{formatRelative((r as unknown as Schedule).next_run_at)}</span>
            ),
            width: '14ch',
          },
          {
            key: 'enabled',
            label: 'Estado',
            width: '10ch',
            render: (r) => {
              const e = (r as unknown as Schedule).enabled;
              return (
                <span class={`text-xs font-medium ${e ? 'text-success' : 'text-muted-foreground'}`}>
                  {e ? 'ativo' : 'inativo'}
                </span>
              );
            },
          },
          {
            key: 'actions',
            label: '',
            width: '16ch',
            render: (r) => {
              const s = r as unknown as Schedule;
              return (
                <div class="flex items-center gap-1">
                  <IconBtn
                    title="Disparar agora"
                    onClick={() => trigger(s.id)}
                    icon={<i class="ph ph-play text-sm" />}
                  />
                  <IconBtn
                    title={s.enabled ? 'Desativar' : 'Ativar'}
                    onClick={() => toggleEnabled(s)}
                    icon={<i class="ph ph-power text-sm" />}
                    tone={s.enabled ? 'default' : 'muted'}
                  />
                  <IconBtn
                    title="Editar"
                    onClick={() => openEdit(s)}
                    icon={<i class="ph ph-pencil-simple text-sm" />}
                  />
                  <IconBtn
                    title="Excluir"
                    onClick={() => remove(s.id)}
                    icon={<i class="ph ph-trash text-sm" />}
                    tone="destructive"
                  />
                </div>
              );
            },
          },
        ]}
        empty={activeToken.value ? 'Nenhum agendamento. Crie um para começar.' : 'Configure o token nas configurações para listar agendamentos.'}
      />

      <ScheduleEditor
        open={editorOpen}
        initial={editing}
        onClose={() => setEditorOpen(false)}
        onSaved={refresh}
      />
    </div>
  );
}

function IconBtn({
  title,
  onClick,
  icon,
  tone = 'default',
}: {
  title: string;
  onClick: () => void;
  icon: preact.ComponentChildren;
  tone?: 'default' | 'muted' | 'destructive';
}) {
  const toneClass =
    tone === 'destructive'
      ? 'text-muted-foreground hover:text-destructive hover:bg-destructive/10'
      : tone === 'muted'
        ? 'text-muted-foreground/60 hover:text-foreground hover:bg-accent'
        : 'text-muted-foreground hover:text-foreground hover:bg-accent';
  return (
    <button
      class={`inline-flex items-center justify-center size-8 rounded-md focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 ${toneClass}`}
      onClick={onClick}
      title={title}
      aria-label={title}
    >
      {icon}
    </button>
  );
}
